/**
 * IO Delivery Health widget — currently DELIVERING line items from GAM with
 * pacing classification (under / on / over), plus READY items launching in
 * the next 7 days. Source: GAM SOAP via the shared `gamReportService`
 * plumbing the io-reconciliation skill already uses.
 */

import React, { useMemo, useState } from 'react';
import { StatusPill, tableStyles } from '../ui';
import type { StatusVariant } from '../ui';
import { IoDeliveryResponse, IoDeliveryItem } from '../../services/salesApi';
import WidgetHeader from './WidgetHeader';
import FilterChipGroup from './FilterChipGroup';

interface Props {
  loading: boolean;
  error: string | null;
  data: IoDeliveryResponse | null;
}

type FilterKey = 'all' | 'delivering' | 'launching_soon' | 'under_pacing' | 'over_pacing' | 'ending_soon';
type SortKey = 'name' | 'advertiser' | 'order' | 'delivered' | 'delta' | 'ends';
type SortDir = 'asc' | 'desc';

const FILTERS: { value: FilterKey; label: string }[] = [
  { value: 'all',            label: 'All' },
  { value: 'delivering',     label: 'Delivering' },
  { value: 'launching_soon', label: 'Launching ≤7d' },
  { value: 'under_pacing',   label: 'Under-pacing' },
  { value: 'over_pacing',    label: 'Over-pacing' },
  { value: 'ending_soon',    label: 'Ending in 14d' },
];

const formatPercent = (n: number | undefined): string =>
  n === undefined ? '—' : `${n.toFixed(0)}%`;

const formatImpressions = (n: number | undefined): string =>
  n === undefined ? '—' : n.toLocaleString();

const pacingVariant = (status: IoDeliveryItem['pacingStatus']): StatusVariant => {
  if (status === 'under_pacing') return 'flagged';
  if (status === 'over_pacing') return 'pending';
  if (status === 'on_pace') return 'live';
  return 'draft';
};

const pacingLabel = (status: IoDeliveryItem['pacingStatus']): string => {
  if (status === 'under_pacing') return 'Under';
  if (status === 'over_pacing') return 'Over';
  if (status === 'on_pace') return 'On pace';
  return 'Unknown';
};

const pacingDeltaColor = (delta: number | undefined): string => {
  if (delta === undefined) return 'var(--fg-4)';
  if (delta < -5) return 'var(--danger)';
  if (delta > 5) return 'var(--warning)';
  return 'var(--success)';
};

const compareNullable = <T extends string | number>(
  a: T | undefined,
  b: T | undefined,
  dir: SortDir,
): number => {
  if (a === undefined && b === undefined) return 0;
  if (a === undefined) return 1;  // undefined always sorts last
  if (b === undefined) return -1;
  const cmp = typeof a === 'string' && typeof b === 'string'
    ? a.localeCompare(b, undefined, { sensitivity: 'base' })
    : a < b ? -1 : a > b ? 1 : 0;
  return dir === 'asc' ? cmp : -cmp;
};

// First-click default direction per sort key. Worst-pacers-first means delta ascending.
const SORT_DEFAULT_DIR: Record<SortKey, SortDir> = {
  name:       'asc',
  advertiser: 'asc',
  order:      'asc',
  delivered:  'desc',
  delta:      'asc',
  ends:       'asc',
};

const IoDeliveryHealth: React.FC<Props> = ({ loading, error, data }) => {
  const [filter, setFilter] = useState<FilterKey>('all');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('delta');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const items = useMemo(() => data?.items ?? [], [data]);

  const filtered = useMemo(() => {
    let result = items;
    if (filter === 'delivering') {
      result = result.filter((i) => i.phase === 'delivering');
    } else if (filter === 'launching_soon') {
      result = result.filter(
        (i) => i.phase === 'launching_soon' && i.daysUntilStart !== undefined && i.daysUntilStart >= 0 && i.daysUntilStart <= 7,
      );
    } else if (filter === 'ending_soon') {
      result = result.filter((i) => i.daysUntilEnd !== undefined && i.daysUntilEnd >= 0 && i.daysUntilEnd <= 14);
    } else if (filter === 'under_pacing' || filter === 'over_pacing') {
      result = result.filter((i) => i.pacingStatus === filter);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter((i) =>
        (i.name || '').toLowerCase().includes(q) ||
        (i.orderName || '').toLowerCase().includes(q) ||
        (i.advertiserName || '').toLowerCase().includes(q),
      );
    }
    return result;
  }, [items, filter, search]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      switch (sortKey) {
        case 'name':       return compareNullable(a.name, b.name, sortDir);
        case 'advertiser': return compareNullable(a.advertiserName, b.advertiserName, sortDir);
        case 'order':      return compareNullable(a.orderName, b.orderName, sortDir);
        case 'delivered':  return compareNullable(a.percentDelivered, b.percentDelivered, sortDir);
        case 'delta':      return compareNullable(a.pacingDelta, b.pacingDelta, sortDir);
        case 'ends':       return compareNullable(a.endDate, b.endDate, sortDir);
        default:           return 0;
      }
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(SORT_DEFAULT_DIR[key]);
    }
  };

  const sortIndicator = (key: SortKey): string => {
    if (sortKey !== key) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  const subtitle = loading
    ? 'Querying GAM…'
    : data
      ? `${data.totals.delivering} delivering · ${data.totals.launchingSoon} launching ≤7d · ${data.totals.underPacing} under · ${data.totals.overPacing} over · ${data.totals.endingSoon} ending soon`
      : '';

  const warnings = data?.warnings ?? [];

  return (
    <div style={styles.container}>
      <WidgetHeader
        title="IO delivery health"
        subtitle={subtitle}
        right={
          <div style={styles.controlsRow}>
            <input
              type="text"
              placeholder="Search line item, order, advertiser…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={styles.searchInput}
            />
            <FilterChipGroup options={FILTERS} value={filter} onChange={setFilter} />
          </div>
        }
      />

      {warnings.length > 0 && (
        <div style={styles.warningBanner}>
          {warnings.map((w, idx) => (
            <div key={idx}>⚠ {w}</div>
          ))}
        </div>
      )}

      {error && <div style={styles.error}>{error}</div>}

      {!loading && !error && sorted.length === 0 && (
        <div style={styles.empty}>
          {items.length === 0
            ? 'No DELIVERING or READY line items found in GAM right now.'
            : 'No line items match the current filter.'}
        </div>
      )}

      {!loading && sorted.length > 0 && (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.thSortable} onClick={() => handleSort('name')}>
                  Line item{sortIndicator('name')}
                </th>
                <th style={styles.thSortable} onClick={() => handleSort('advertiser')}>
                  Advertiser{sortIndicator('advertiser')}
                </th>
                <th style={styles.thSortable} onClick={() => handleSort('order')}>
                  Order{sortIndicator('order')}
                </th>
                <th style={styles.thSortableRight} onClick={() => handleSort('delivered')}>
                  Delivered{sortIndicator('delivered')}
                </th>
                <th style={styles.thRight}>Expected</th>
                <th style={styles.thSortableRight} onClick={() => handleSort('delta')}>
                  Δ{sortIndicator('delta')}
                </th>
                <th style={styles.thSortable} onClick={() => handleSort('ends')}>
                  Ends{sortIndicator('ends')}
                </th>
                <th style={styles.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((item) => {
                const impressionsTooltip =
                  item.impressionsDelivered !== undefined && item.impressionsGoal !== undefined
                    ? `${formatImpressions(item.impressionsDelivered)} / ${formatImpressions(item.impressionsGoal)} impressions`
                    : undefined;
                const isLaunchingSoon = item.phase === 'launching_soon';
                return (
                  <tr key={item.lineItemId}>
                    <td style={styles.td}>
                      {isLaunchingSoon && (
                        <span style={styles.launchBadge} title={
                          item.daysUntilStart !== undefined && item.daysUntilStart >= 0
                            ? `Launches in ${item.daysUntilStart}d`
                            : 'Ready to launch'
                        }>
                          ⏱ Launching
                        </span>
                      )}
                      {item.gamUrl ? (
                        <a href={item.gamUrl} target="_blank" rel="noopener noreferrer" style={styles.link}>
                          {item.name}
                        </a>
                      ) : (
                        <span style={styles.cellText}>{item.name}</span>
                      )}
                    </td>
                    <td style={styles.td}>
                      <span style={styles.cellText}>{item.advertiserName || '—'}</span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.subtle}>
                        {item.orderName || item.orderId || '—'}
                      </span>
                    </td>
                    <td style={styles.tdRight} title={impressionsTooltip}>
                      {formatPercent(item.percentDelivered)}
                    </td>
                    <td style={styles.tdRight}>{formatPercent(item.expectedPercent)}</td>
                    <td style={{
                      ...styles.tdRight,
                      color: pacingDeltaColor(item.pacingDelta),
                      fontWeight: 600,
                    }}>
                      {item.pacingDelta === undefined
                        ? '—'
                        : `${item.pacingDelta > 0 ? '+' : ''}${item.pacingDelta.toFixed(0)}pt`}
                    </td>
                    <td style={styles.td}>
                      {item.endDate ? (
                        <span style={styles.cellText}>
                          {item.endDate}
                          {item.daysRemaining !== undefined && (
                            <span style={styles.subtle}> · {item.daysRemaining}d left</span>
                          )}
                        </span>
                      ) : '—'}
                    </td>
                    <td style={styles.td}>
                      <StatusPill variant={pacingVariant(item.pacingStatus)} size="sm">
                        {pacingLabel(item.pacingStatus)}
                      </StatusPill>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: { padding: 20, display: 'flex', flexDirection: 'column', gap: 14 },
  controlsRow: { display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' },
  searchInput: {
    padding: '6px 10px',
    fontSize: 12,
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-md)',
    background: 'var(--bg-surface-1)',
    color: 'var(--fg-1)',
    fontFamily: 'var(--font-sans)',
    minWidth: 240,
  },
  warningBanner: {
    padding: '10px 12px',
    background: 'var(--warning-soft)',
    color: 'var(--warning)',
    borderRadius: 'var(--radius-md)',
    fontSize: 12,
    border: '1px solid var(--warning)',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  error: {
    padding: '10px 12px',
    background: 'var(--danger-soft)',
    color: 'var(--danger)',
    borderRadius: 'var(--radius-md)',
    fontSize: 13,
  },
  empty: {
    padding: '24px 12px',
    textAlign: 'center',
    color: 'var(--fg-4)',
    fontSize: 13,
  },
  tableWrap: tableStyles.wrapScroll,
  table: tableStyles.table,
  th: tableStyles.th,
  thRight: tableStyles.thNum,
  thSortable: {
    ...tableStyles.th,
    cursor: 'pointer',
    userSelect: 'none',
  },
  thSortableRight: {
    ...tableStyles.thNum,
    cursor: 'pointer',
    userSelect: 'none',
  },
  td: {
    ...tableStyles.td,
    verticalAlign: 'middle',
  },
  tdRight: {
    ...tableStyles.tdNum,
    verticalAlign: 'middle',
  },
  cellText: { fontSize: 13 },
  subtle: { color: 'var(--fg-4)', fontSize: 12 },
  link: { color: 'var(--accent)', textDecoration: 'none', fontWeight: 600 },
  launchBadge: {
    display: 'inline-block',
    padding: '2px 6px',
    marginRight: 6,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.04em',
    color: 'var(--accent)',
    background: 'var(--accent-soft)',
    borderRadius: 'var(--radius-pill)',
    textTransform: 'uppercase',
  },
};

export default IoDeliveryHealth;
